import React, { useState } from 'react';
import { SEOHead } from '../components/SEOHead';
import {
    YoutubeLogo,
    Envelope,
    Phone,
    WhatsappLogo,
    MapPin,
    ArrowRight,
    Sparkle
} from '@phosphor-icons/react';
import logoPath from '../logo.webp';

export const AboutUs: React.FC = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatus('sending');
        // Simulate form submission
        setTimeout(() => {
            setStatus('success');
            setFormData({ name: '', email: '', subject: '', message: '' });
        }, 1500);
    };

    const shows = [
        {
            name: "Spill The Rage Show",
            image: "/rage.jpeg",
            link: "https://youtube.com/@spilltherage1?si=eLmpmYC9NWv8ZXQ8",
            description: "Dive deep into the conversations that spark emotion and change."
        },
        {
            name: "Wild Trends Show",
            image: "/trends.jpeg",
            link: "https://youtube.com/@wildtrendz_1?si=jd6Lvn69i0ko4vOE",
            description: "Exploring the wildest trends shaping our culture today."
        }
    ];

    return (
        <div className="animate-fade-in pb-20">
            <SEOHead
                title="About Us - Nida Studios"
                description="Learn about Nida Studios, our premium brand, our shows, and how to reach us."
            />

            {/* Hero Section */}
            <section className="relative py-20 bg-brand-950 text-white overflow-hidden rounded-3xl mb-20">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-brand-400 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2"></div>
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-brand-500 rounded-full blur-[120px] translate-x-1/2 translate-y-1/2"></div>
                </div>

                <div className="relative max-w-4xl mx-auto px-6 text-center">
                    <img src={logoPath} alt="Nida logo" className="mx-auto w-32 h-auto mb-8 animate-float" />
                    <h1 className="text-5xl md:text-7xl font-serif font-black mb-6 tracking-tight leading-tight">
                        Nida Studios
                    </h1>
                    <p className="text-xl md:text-2xl text-gray-300 font-serif leading-relaxed max-w-3xl mx-auto italic">
                        "In-Depth Politics, Trending Conversations & Premium Entertainment"
                    </p>
                </div>
            </section>

            {/* The Brand Section */}
            <section className="max-w-7xl mx-auto px-6 mb-32">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    <div className="space-y-8">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand-100 text-brand-900 rounded-full text-xs font-black uppercase tracking-widest">
                            <Sparkle weight="fill" /> The Brand
                        </div>
                        <h2 className="text-4xl md:text-5xl font-serif font-black text-brand-900 leading-tight">
                            Illuminating Truth, <br />
                            <span className="text-brand-500">Inspiring Dialogue.</span>
                        </h2>
                        <div className="space-y-6 text-lg text-gray-600 font-serif leading-relaxed">
                            <p>
                                Nida Studios is a modern media powerhouse dedicated to delivering accurate, engaging, and impactful content that informs, educates, and inspires our audience.
                            </p>
                            <p>
                                In an era of rapid information, we stand as a lighthouse for rigorous journalism and creative excellence. Through innovative storytelling and digital creativity, we cover the critical issues that shape our world today—from the corridors of power to the pulse of cultural trends.
                            </p>
                            <p>
                                Our mission is simple: to remain a trusted voice, connect communities, and lead meaningful conversations through high-quality media across all digital platforms.
                            </p>
                        </div>
                    </div>
                    <div className="flex justify-center items-center">
                        <div className="w-full max-w-md aspect-square bg-gray-50 rounded-[2rem] overflow-hidden group border border-gray-100 shadow-inner flex items-center justify-center p-12">
                            <img
                                src={logoPath}
                                className="w-full h-auto object-contain transition-all duration-700 hover:scale-110"
                                alt="Nida Brand"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Shows Showcase */}
            <section className="bg-gray-50 py-24 rounded-[3rem] mb-32">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-serif font-black text-brand-900 mb-4">Our Featured Productions</h2>
                        <p className="text-gray-500 font-serif max-w-2xl mx-auto">Explore the diverse range of shows that define the Nida Studios experience.</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-12">
                        {shows.map((show) => (
                            <div key={show.name} className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100">
                                <div className="aspect-video overflow-hidden relative">
                                    <img
                                        src={show.image}
                                        alt={show.name}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                    />
                                    <div className="absolute inset-0 bg-brand-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                        <a
                                            href={show.link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="bg-white text-brand-900 px-8 py-3 rounded-full font-black text-xs uppercase tracking-widest flex items-center gap-2 transform translate-y-4 group-hover:translate-y-0 transition-all duration-500"
                                        >
                                            Watch on YouTube <YoutubeLogo size={20} weight="fill" className="text-red-600" />
                                        </a>
                                    </div>
                                </div>
                                <div className="p-8">
                                    <h3 className="text-2xl font-serif font-black text-brand-900 mb-3">{show.name}</h3>
                                    <p className="text-gray-500 font-serif mb-6 leading-relaxed">{show.description}</p>
                                    <a
                                        href={show.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 text-brand-500 font-black text-xs uppercase tracking-widest group-hover:gap-4 transition-all"
                                    >
                                        Experience the show <ArrowRight size={16} weight="bold" />
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Contact Us Section */}
            <section className="max-w-7xl mx-auto px-6">
                <div className="text-center mb-16">
                    <h2 className="text-4xl font-serif font-black text-brand-900 mb-4">Get In Touch</h2>
                    <p className="text-gray-500 font-serif max-w-2xl mx-auto">Have a story, a question, or a partnership proposal? We're listening.</p>
                </div>

                <div className="grid lg:grid-cols-12 gap-12 items-start">
                    {/* Form */}
                    <div className="lg:col-span-7 bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase tracking-widest text-gray-400">Your Name</label>
                                    <input
                                        required
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-4 text-brand-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
                                        placeholder="Enter your full name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-black uppercase tracking-widest text-gray-400">Email Address</label>
                                    <input
                                        required
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-4 text-brand-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
                                        placeholder="name@example.com"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-black uppercase tracking-widest text-gray-400">Subject</label>
                                <input
                                    required
                                    type="text"
                                    value={formData.subject}
                                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                    className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-4 text-brand-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
                                    placeholder="What is this about?"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-black uppercase tracking-widest text-gray-400">Message</label>
                                <textarea
                                    required
                                    rows={6}
                                    value={formData.message}
                                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                                    className="w-full bg-gray-50 border border-gray-100 rounded-xl px-4 py-4 text-brand-900 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all resize-none"
                                    placeholder="How can we help you?"
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={status === 'sending'}
                                className="w-full bg-brand-900 text-white font-black text-xs uppercase tracking-widest py-5 rounded-xl hover:bg-brand-500 transition-all transform hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-3 disabled:opacity-70"
                            >
                                {status === 'sending' ? (
                                    <>Processing...</>
                                ) : (
                                    <>Send Message <ArrowRight weight="bold" /></>
                                )}
                            </button>

                            {status === 'success' && (
                                <div className="p-4 bg-green-50 border border-green-100 text-green-700 rounded-xl text-sm font-bold animate-fade-in">
                                    Thank you! Your message has been received. We'll get back to you soon.
                                </div>
                            )}
                        </form>
                    </div>

                    {/* Contact Details */}
                    <div className="lg:col-span-5 space-y-8">
                        <div className="bg-brand-900 text-white rounded-3xl p-10 shadow-xl overflow-hidden relative">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-brand-400/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

                            <h3 className="text-2xl font-serif font-black mb-8">Contact Information</h3>

                            <div className="space-y-8">
                                <div className="flex gap-4 group">
                                    <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center shrink-0 group-hover:bg-brand-400 transition-colors">
                                        <Phone size={24} weight="duotone" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Call Us</p>
                                        <p className="text-lg font-bold hover:text-brand-400 transition-colors">+233558116041</p>
                                    </div>
                                </div>

                                <div className="flex gap-4 group">
                                    <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center shrink-0 group-hover:bg-brand-400 transition-colors">
                                        <WhatsappLogo size={24} weight="duotone" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">WhatsApp</p>
                                        <p className="text-lg font-bold hover:text-brand-400 transition-colors">+233506692176</p>
                                    </div>
                                </div>

                                <div className="flex gap-4 group">
                                    <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center shrink-0 group-hover:bg-brand-400 transition-colors">
                                        <Envelope size={24} weight="duotone" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Email Details</p>
                                        <a href="mailto:nidatv5050@gmail.com" className="text-lg font-bold hover:text-brand-400 transition-colors underline decoration-brand-400 underline-offset-4">nidatv5050@gmail.com</a>
                                    </div>
                                </div>

                                <div className="flex gap-4 group">
                                    <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center shrink-0 group-hover:bg-brand-400 transition-colors">
                                        <MapPin size={24} weight="duotone" />
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-1">Headquarters</p>
                                        <p className="text-lg font-bold leading-tight">Akweteyman, Akachachi <br />Achimota SDA Church Road</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-brand-50 border border-brand-100 rounded-3xl p-8">
                            <h4 className="text-xs font-black uppercase tracking-widest text-brand-900 mb-4 tracking-widest">Office Hours</h4>
                            <div className="space-y-2 text-sm text-gray-600 font-bold uppercase tracking-wide">
                                <div className="flex justify-between"><span>Mon - Fri</span> <span>9:00 AM - 6:00 PM</span></div>
                                <div className="flex justify-between"><span>Saturday</span> <span>10:00 AM - 4:00 PM</span></div>
                                <div className="flex justify-between text-brand-500"><span>Sunday</span> <span>By appointment</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};
